import { useRef } from 'react';
import clsx from 'clsx';
import {
  Button,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  Paper,
  Select,
  TextField,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  CheckCircleRounded as ActiveIcon,
  EcoRounded as EcoIcon,
  EmojiNatureRounded as WildIcon,
  FlagRounded as FrenchIcon,
  PanoramaRounded as PanoramaIcon,
  PhotoCameraRounded as PhotoCameraIcon,
  RoomRounded as LocalIcon,
  SpaRounded as OrganicIcon,
  StarRounded as TopIcon,
} from '@material-ui/icons';
import { Autocomplete } from '@material-ui/lab';

import { Basket } from '../../types/basket.type';
import { BASKET_SIZES } from '../../types/basket-size.type';
import { UpdateBasketRequestDTO } from '../../types/dtos/update-basket.dto';
import { useBasketEditForm } from '../../hooks/use-basket-edit-form.hook';
import { buildBasketUpdatePayload } from '../../utils/build-basket-update-payload.util';
import { useUploadBasketImage } from '../../hooks/upload-basket-image.hook';
import { useAdminGetCategories } from '../../../categories/hooks/admin-get-categories.hook';
import { useAdminGetSubCategories } from '../../../categories/hooks/admin-get-sub-categories.hook';
import { useAdminGetTaxes } from '../../../taxes/hooks/admin-get-taxes.hook';
import { useAdminGetTags } from '../../../tags/hooks/admin-get-tags.hook';
import { useAdminGetWeightUnities } from '../../../weight-unities/hooks/admin-get-weight-unities.hook';
import { Category } from '../../../categories/types/category.type';
import { SubCategory } from '../../../categories/types/sub-category.type';
import { TaxWithUsage } from '../../../taxes/types/tax.type';
import { Tag } from '../../../tags/types/tag.type';
import { WeightUnity } from '../../../weight-unities/types/weight-unity.type';
import { BASKET_IMAGE_PATH } from '../../constants/baskets-app-paths.constants';
import RichTextEditor from '../../../shared/components/Forms/RichTextEditor.component';

type Props = {
  basket: Basket;
  isSubmitting: boolean;
  updateBasket: (basketData: UpdateBasketRequestDTO) => Promise<void>;
  refreshBasket: () => Promise<void>;
};

const DATE_INPUT_VALUE_LENGTH = 'YYYY-MM-DD'.length;

const NAME_MIN_LENGTH = 2;

const toDateInputValue = (isoDate?: string): string => (isoDate ?? '').slice(0, DATE_INPUT_VALUE_LENGTH);

const useStyles = makeStyles((theme) => ({
  root: {
    overflow: 'auto',

    '& h4': {
      margin: '0 0 0.5rem 0',
      opacity: 0.7,
    },

    '& .MuiButton-root': {
      textTransform: 'none',
    },

    '& .MuiOutlinedInput-root.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.admin.primary.main,
    },
    '& .MuiInputLabel-outlined.Mui-focused:not(.Mui-error)': {
      color: theme.palette.admin.primary.main,
    },
  },
  actionsHeader: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: '0.75rem',
  },
  chipRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    flexWrap: 'wrap',
  },
  chip: {
    cursor: 'pointer',
    borderRadius: 4,
    fontWeight: 600,
    fontSize: 12,

    '& .MuiChip-icon': {
      fontSize: 14,
      marginLeft: 6,
      marginRight: -4,
    },
  },
  chipSelected: {
    border: '1px solid transparent',
    backgroundColor: theme.palette.admin.primary.main,
    color: theme.palette.admin.primary.contrastText,

    '& .MuiChip-icon': {
      color: theme.palette.admin.primary.contrastText,
    },

    '&:hover, &:focus': {
      backgroundColor: theme.palette.admin.primary.dark,
    },
  },
  chipDivider: {
    width: 1,
    height: 20,
    backgroundColor: '#ccc',
    marginLeft: '0.25rem',
    marginRight: '0.25rem',
  },
  saveButton: {
    backgroundColor: theme.palette.admin.primary.main,
    color: theme.palette.admin.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.admin.primary.dark,
    },
  },
  paperContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    padding: '1rem 0.75rem',
    height: 'calc(100% - 2rem)',
  },
  identityTop: {
    display: 'flex',
    gap: '1rem',
  },
  imageContainer: {
    position: 'relative',
    minWidth: 150,
    width: 150,
    height: 150,
    borderRadius: 4,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    backgroundColor: '#fafafa',
    border: '1px solid #e0e0e0',
    cursor: 'pointer',

    '&:hover $imageOverlay': {
      opacity: 1,
    },
  },
  imagePreview: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  imageIcon: {
    fontSize: 40,
    color: '#ccc',
  },
  imageOverlay: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(34, 40, 49, 0.55)',
    color: '#ffffff',
    opacity: 0,
    transition: 'opacity 0.2s',
  },
  identityFields: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    flex: 1,
  },
  row: {
    display: 'flex',
    gap: '1rem',

    '& > *': {
      flex: 1,
    },
  },
}));

export default function BasketInformationsTab({ basket, isSubmitting, updateBasket, refreshBasket }: Props) {
  const classes = useStyles();
  const {
    isEditing,
    updatedBasket,
    handleFieldChange,
    handleCategoryChange,
    handleSubCategoryChange,
    handleCancelEditing,
    handleSave,
  } = useBasketEditForm(basket);

  const { uploadBasketImage, isSubmitting: isUploading } = useUploadBasketImage();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { categories } = useAdminGetCategories();
  const { subCategories } = useAdminGetSubCategories({ categoryId: updatedBasket.category?.id });
  const { taxes } = useAdminGetTaxes();
  const { tags } = useAdminGetTags();
  const { weightUnities } = useAdminGetWeightUnities();

  const sortedCategories = [...categories].sort((first, second) => first.name.localeCompare(second.name));
  const sortedSubCategories = [...subCategories].sort((first, second) => first.name.localeCompare(second.name));

  const selectedCategory = sortedCategories.find((category) => category.id === updatedBasket.category?.id) ?? null;
  const selectedSubCategory =
    sortedSubCategories.find((subCategory) => subCategory.id === updatedBasket.subCategory?.id) ?? null;
  const selectedTax = taxes.find((tax) => tax.id === updatedBasket.tax?.id) ?? null;
  const selectedWeightUnity =
    weightUnities.find((weightUnity) => weightUnity.id === updatedBasket.weight?.unity?.id) ?? null;

  const isArchived = Boolean(basket.archivedAt);
  const trimmedName = updatedBasket.name.trim();
  const isNameInvalid = trimmedName.length < NAME_MIN_LENGTH;
  const nameHelperText = trimmedName === '' ? 'Le nom est requis' : 'Le nom doit contenir au moins 2 caractères';
  const isSubCategoryInvalid = !updatedBasket.subCategory?.id;
  const isPriceInvalid = updatedBasket.price <= 0;
  const isStockInvalid = updatedBasket.stock < 0;
  const hasValidationErrors = isNameInvalid || isSubCategoryInvalid || isPriceInvalid || isStockInvalid;

  const handleImageSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const uploadedKey = await uploadBasketImage(basket.id, file);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (uploadedKey) await refreshBasket();
  };

  const saveEditedFields = async (editedFields: Partial<Basket>) => {
    const basketData = buildBasketUpdatePayload(editedFields);
    if (Object.keys(basketData).length === 0) return;

    await updateBasket(basketData);
  };

  const toggleLabel = (labelKey: 'organic' | 'wild' | 'french' | 'native') =>
    handleFieldChange({
      field: 'labels',
      value: { ...updatedBasket.labels, [labelKey]: !updatedBasket.labels?.[labelKey] },
    });

  return (
    <section className={classes.root}>
      <Grid container spacing={2}>
        <Grid item xs={12} className={classes.actionsHeader}>
          <Button size="small" variant="text" onClick={handleCancelEditing} disabled={!isEditing || isSubmitting}>
            Annuler
          </Button>
          <Button
            size="small"
            variant="contained"
            className={classes.saveButton}
            onClick={() => handleSave(saveEditedFields)}
            disabled={!isEditing || isSubmitting || hasValidationErrors}
            startIcon={isSubmitting ? <CircularProgress size={16} /> : null}
          >
            {isSubmitting ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </Grid>

        <Grid item xs={12} md={6}>
          <section>
            <h4>Identité</h4>
            <Paper className={classes.paperContent}>
              <div className={classes.identityTop}>
                <div
                  className={classes.imageContainer}
                  title="Changer l'image"
                  onClick={() => !isUploading && fileInputRef.current?.click()}
                >
                  {isUploading ? (
                    <CircularProgress size={40} />
                  ) : updatedBasket.photo ? (
                    <img
                      src={BASKET_IMAGE_PATH(updatedBasket.photo)}
                      alt={updatedBasket.name}
                      className={classes.imagePreview}
                    />
                  ) : (
                    <PanoramaIcon className={classes.imageIcon} />
                  )}
                  {!isUploading && (
                    <div className={classes.imageOverlay}>
                      <PhotoCameraIcon />
                    </div>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/jpg,image/png"
                  hidden
                  onChange={handleImageSelected}
                />

                <div className={classes.identityFields}>
                  <TextField
                    size="small"
                    variant="outlined"
                    fullWidth
                    required
                    label="Nom"
                    error={isNameInvalid}
                    helperText={isNameInvalid ? nameHelperText : undefined}
                    value={updatedBasket.name}
                    onChange={(event) => handleFieldChange({ field: 'name', value: event.target.value })}
                  />
                  <TextField
                    size="small"
                    variant="outlined"
                    fullWidth
                    label="Référence"
                    value={updatedBasket.reference}
                    onChange={(event) => handleFieldChange({ field: 'reference', value: event.target.value })}
                  />
                </div>
              </div>
              <RichTextEditor
                value={updatedBasket.description}
                onChange={(html) => handleFieldChange({ field: 'description', value: html })}
                placeholder="Description du panier"
              />
            </Paper>
          </section>
        </Grid>

        <Grid item xs={12} md={6}>
          <section>
            <h4>Caractéristiques</h4>
            <Paper className={classes.paperContent}>
              <div className={classes.chipRow}>
                <Chip
                  className={clsx(classes.chip, updatedBasket.isActive && classes.chipSelected)}
                  label="Actif"
                  icon={<ActiveIcon />}
                  size="small"
                  variant={updatedBasket.isActive ? 'default' : 'outlined'}
                  disabled={isArchived}
                  onClick={() => handleFieldChange({ field: 'isActive', value: !updatedBasket.isActive })}
                />
                <div className={classes.chipDivider} />
                <Chip
                  className={clsx(classes.chip, updatedBasket.labels?.organic && classes.chipSelected)}
                  label="Bio"
                  icon={<OrganicIcon />}
                  size="small"
                  variant={updatedBasket.labels?.organic ? 'default' : 'outlined'}
                  onClick={() => toggleLabel('organic')}
                />
                <Chip
                  className={clsx(classes.chip, updatedBasket.labels?.wild && classes.chipSelected)}
                  label="Sauvage"
                  icon={<WildIcon />}
                  size="small"
                  variant={updatedBasket.labels?.wild ? 'default' : 'outlined'}
                  onClick={() => toggleLabel('wild')}
                />
                <Chip
                  className={clsx(classes.chip, updatedBasket.labels?.french && classes.chipSelected)}
                  label="Français"
                  icon={<FrenchIcon />}
                  size="small"
                  variant={updatedBasket.labels?.french ? 'default' : 'outlined'}
                  onClick={() => toggleLabel('french')}
                />
                <Chip
                  className={clsx(classes.chip, updatedBasket.labels?.native && classes.chipSelected)}
                  label="Local"
                  icon={<LocalIcon />}
                  size="small"
                  variant={updatedBasket.labels?.native ? 'default' : 'outlined'}
                  onClick={() => toggleLabel('native')}
                />
                <div className={classes.chipDivider} />
                <Chip
                  className={clsx(classes.chip, updatedBasket.isTop && classes.chipSelected)}
                  label="Top"
                  icon={<TopIcon />}
                  size="small"
                  variant={updatedBasket.isTop ? 'default' : 'outlined'}
                  onClick={() => handleFieldChange({ field: 'isTop', value: !updatedBasket.isTop })}
                />
                <Chip
                  className={clsx(classes.chip, updatedBasket.isEco && classes.chipSelected)}
                  label="Éco"
                  icon={<EcoIcon />}
                  size="small"
                  variant={updatedBasket.isEco ? 'default' : 'outlined'}
                  onClick={() => handleFieldChange({ field: 'isEco', value: !updatedBasket.isEco })}
                />
              </div>

              <div className={classes.row}>
                <Autocomplete
                  fullWidth
                  size="small"
                  value={selectedCategory}
                  options={sortedCategories}
                  groupBy={(option) => option.name[0].toUpperCase()}
                  getOptionLabel={(option) => option.name}
                  getOptionSelected={(option, selected) => option.id === selected.id}
                  onChange={(event, selectedOption: Category | null) => handleCategoryChange(selectedOption)}
                  renderInput={(params) => <TextField {...params} size="small" variant="outlined" label="Rubrique" />}
                />
                <Autocomplete
                  fullWidth
                  size="small"
                  value={selectedSubCategory}
                  disabled={!selectedCategory}
                  options={sortedSubCategories}
                  groupBy={(option) => option.name[0].toUpperCase()}
                  getOptionLabel={(option) => option.name}
                  getOptionSelected={(option, selected) => option.id === selected.id}
                  onChange={(event, selectedOption: SubCategory | null) => handleSubCategoryChange(selectedOption)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      variant="outlined"
                      label="Sous-rubrique"
                      error={isSubCategoryInvalid}
                      helperText={isSubCategoryInvalid ? 'La sous-rubrique est requise' : undefined}
                    />
                  )}
                />
              </div>

              <div className={classes.row}>
                <TextField
                  size="small"
                  variant="outlined"
                  fullWidth
                  type="number"
                  label="Prix"
                  error={isPriceInvalid}
                  helperText={isPriceInvalid ? 'Le prix doit être supérieur à 0' : undefined}
                  value={updatedBasket.price}
                  onChange={(event) => handleFieldChange({ field: 'price', value: Number(event.target.value) })}
                  InputProps={{ endAdornment: <InputAdornment position="end">€</InputAdornment> }}
                />
                <Autocomplete
                  fullWidth
                  size="small"
                  value={selectedTax}
                  options={[...taxes].sort((first, second) => first.name.localeCompare(second.name))}
                  getOptionLabel={(option) => option.name}
                  getOptionSelected={(option, selected) => option.id === selected.id}
                  onChange={(event, selectedOption: TaxWithUsage | null) => {
                    if (selectedOption) {
                      handleFieldChange({
                        field: 'tax',
                        value: { id: selectedOption.id, name: selectedOption.name, amount: selectedOption.amount },
                      });
                    }
                  }}
                  renderInput={(params) => <TextField {...params} size="small" variant="outlined" label="TVA" />}
                />
                <FormControl variant="outlined" size="small" fullWidth>
                  <InputLabel>Taille</InputLabel>
                  <Select
                    native
                    label="Taille"
                    value={updatedBasket.size}
                    onChange={(event) => handleFieldChange({ field: 'size', value: event.target.value as string })}
                  >
                    {Object.values(BASKET_SIZES).map((basketSize) => (
                      <option key={basketSize} value={basketSize}>
                        {basketSize}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </div>

              <div className={classes.row}>
                <TextField
                  size="small"
                  variant="outlined"
                  fullWidth
                  type="number"
                  label="Poids min"
                  value={updatedBasket.weight?.from ?? ''}
                  onChange={(event) =>
                    handleFieldChange({
                      field: 'weight',
                      value: { ...updatedBasket.weight, from: Number(event.target.value) },
                    })
                  }
                />
                <TextField
                  size="small"
                  variant="outlined"
                  fullWidth
                  type="number"
                  label="Poids max"
                  value={updatedBasket.weight?.to ?? ''}
                  onChange={(event) =>
                    handleFieldChange({
                      field: 'weight',
                      value: { ...updatedBasket.weight, to: Number(event.target.value) },
                    })
                  }
                />
                <Autocomplete
                  fullWidth
                  size="small"
                  value={selectedWeightUnity}
                  options={[...weightUnities].sort((first, second) => first.name.localeCompare(second.name))}
                  getOptionLabel={(option) => `${option.name} (${option.abbreviation})`}
                  getOptionSelected={(option, selected) => option.id === selected.id}
                  onChange={(event, selectedOption: WeightUnity | null) => {
                    if (selectedOption) {
                      handleFieldChange({
                        field: 'weight',
                        value: {
                          ...updatedBasket.weight,
                          unity: {
                            id: selectedOption.id,
                            name: selectedOption.name,
                            abbreviation: selectedOption.abbreviation,
                          },
                        },
                      });
                    }
                  }}
                  renderInput={(params) => (
                    <TextField {...params} size="small" variant="outlined" label="Unité de poids" />
                  )}
                />
              </div>

              <div className={classes.row}>
                <TextField
                  size="small"
                  variant="outlined"
                  fullWidth
                  type="number"
                  label="Personnes min"
                  value={updatedBasket.persons?.from ?? ''}
                  onChange={(event) =>
                    handleFieldChange({
                      field: 'persons',
                      value: { ...updatedBasket.persons, from: Number(event.target.value) },
                    })
                  }
                />
                <TextField
                  size="small"
                  variant="outlined"
                  fullWidth
                  type="number"
                  label="Personnes max"
                  value={updatedBasket.persons?.to ?? ''}
                  onChange={(event) =>
                    handleFieldChange({
                      field: 'persons',
                      value: { ...updatedBasket.persons, to: Number(event.target.value) },
                    })
                  }
                />
                <TextField
                  size="small"
                  variant="outlined"
                  fullWidth
                  type="number"
                  label="Stock"
                  error={isStockInvalid}
                  helperText={isStockInvalid ? 'Le stock ne peut pas être négatif' : undefined}
                  value={updatedBasket.stock}
                  onChange={(event) => handleFieldChange({ field: 'stock', value: Number(event.target.value) })}
                />
              </div>

              <div className={classes.row}>
                <TextField
                  size="small"
                  variant="outlined"
                  fullWidth
                  type="date"
                  id="basket-availability-from"
                  label="Disponible du"
                  value={toDateInputValue(updatedBasket.availability?.from)}
                  onChange={(event) =>
                    handleFieldChange({
                      field: 'availability',
                      value: { ...updatedBasket.availability, from: event.target.value },
                    })
                  }
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  size="small"
                  variant="outlined"
                  fullWidth
                  type="date"
                  id="basket-availability-to"
                  label="Disponible au"
                  value={toDateInputValue(updatedBasket.availability?.to)}
                  onChange={(event) =>
                    handleFieldChange({
                      field: 'availability',
                      value: { ...updatedBasket.availability, to: event.target.value },
                    })
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </div>

              <Autocomplete
                multiple
                fullWidth
                size="small"
                value={tags.filter((tag) => (updatedBasket.tags ?? []).some((basketTag) => basketTag.id === tag.id))}
                options={[...tags].sort((first, second) => first.name.localeCompare(second.name))}
                getOptionLabel={(option) => option.name}
                getOptionSelected={(option, selected) => option.id === selected.id}
                onChange={(event, selectedOptions: Tag[]) =>
                  handleFieldChange({ field: 'tags', value: selectedOptions })
                }
                renderInput={(params) => <TextField {...params} size="small" variant="outlined" label="Tags" />}
              />
            </Paper>
          </section>
        </Grid>
      </Grid>
    </section>
  );
}
